<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="allowCreate ? $t('create') : null"
        :create-mode-text="$t('create_location_point')"
        :edit-mode-text="$t('edit_location_point')"
        @reset="resetData"
    >
        <template v-slot:additional-mode>
            <LocationPointDetailsModal
                :data="selectedLocationPoint"
                @go-to-table="goToTable"
                @load-locations="emit('load-locations')"
            />
        </template>
        <template v-slot:main-mode>
            <slot name="filters" />
            <v-data-table
                v-show="!hideTable"
                :headers="headers"
                :items="items"
                :items-length="items.length"
                :items-per-page="15"
                :items-per-page-options="[15, 25, 50, -1]"
                :loading="isLoading"
                color="primary"
                density="compact"
                :data-cy="`${props.locationType}-table`"
                class="elevation-1"
            >
                <template v-if="props.locationType === 'restricted_location'" v-slot:item.cityName="{item}">
                    <span v-if="item.allCities">{{ t('all_cities') }}</span>
                    <span v-else>
                        {{ getLocaleName(item.cityName) }}
                    </span>
                </template>
                <template v-slot:item.name="{item}">
                    <span
                        v-if="allowEdit"
                        class="text-primary text-decoration-underline cursor-pointer"
                        @click="editLocation(item)"
                    >
                        {{ getLocaleName(item.name) }}
                    </span>
                    <span v-else>
                        {{ getLocaleName(item.name) }}
                    </span>
                </template>
                <template v-slot:item.coordinates="{item}">
                    <span v-if="item.latitude">{{ item.latitude }}</span>
                    <span v-if="item.longitude">
                        {{ `${item.latitude ? ', ' : ''}${item.longitude}` }}
                    </span>
                </template>
                <template v-slot:item.status="{item}">
                    {{ item.status ? item.status?.toLowerCase() : $t('active') }}
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
                <template v-slot:item.actions="{index}">
                    <v-btn
                        color="error"
                        icon="mdi-close"
                        variant="text"
                        size="small"
                        @click="emit('remove-location', index)"
                    />
                </template>
            </v-data-table>
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import type {LocationPointType} from '~/utils/types/account/locations/types'
    import LocationPointDetailsModal from '~/components/account/locations/LocationPointDetailsModal.vue'
    import {initLocationPoint} from '~/utils/types/account/locations/helpers'

    const props = defineProps<{
        locationType: string
        items: any[]
        isLoading: boolean
        hideTable?: boolean
        allowEdit?: boolean
        allowCreate?: boolean
    }>()
    const emit = defineEmits<{
        (e: 'remove-location', value: string): void
        (e: 'load-locations'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const selectedLocationPoint = ref<LocationPointType>(initLocationPoint())
    const modeSwitcher = ref(null)

    const headers: ComputedRef<{title: string; value: string}[]> = computed(() => {
        if (props.locationType === 'restricted_location') {
            const headers: {title: string; key: string; align?: string; width?: string; sortable?: boolean}[] = [
                {title: t('city'), key: 'cityName'},
                {title: t('country'), key: 'countryName'},
                {title: t('actions'), key: 'actions', align: 'end', width: '50px', sortable: false},
            ]

            return headers
        }
        if (props.locationType === 'city') {
            const headers: {title: string; key: string; sortable?: boolean}[] = [
                {title: t('name'), key: 'name'},
                {title: t('country'), key: 'countryName'},
                {title: t('status'), key: 'status', sortable: false},
            ]

            return headers
        }
        if (props.locationType === 'country') {
            const headers: {title: string; key: string; sortable?: boolean}[] = [
                {title: t('country'), key: 'name'},
                {title: t('status'), key: 'status', sortable: false},
            ]

            return headers
        }
        if (props.locationType === 'location_point') {
            const headers: {title: string; key: string; sortable?: boolean}[] = [
                {title: t('name'), key: 'name', sort: customSort},
                {title: t('country'), key: 'countryName'},
                {title: t('city'), key: 'cityName'},
                {title: t('postal_code'), key: 'postalCode'},
                {title: t('street'), key: 'street'},
                {title: t('coordinates'), key: 'coordinates', sortable: false},
                {title: t('status'), key: 'active', sortable: false},
            ]

            return headers
        }

        return []
    })
    const appLocaleCode: ComputedRef<string> = computed(() => app.$i18n.locale.value)

    function getLocaleName(nameObj: object | string): object | string {
        if (!nameObj || (nameObj && !Object.keys(nameObj).length)) return null
        if (typeof nameObj === 'string') return nameObj

        if (nameObj[appLocaleCode.value]) return nameObj[appLocaleCode.value]
        else if (nameObj['en']) return nameObj['en']

        return nameObj[Object.keys(nameObj)[0]]
    }

    function customSort(a: object, b: object): number {
        if (a[appLocaleCode.value] && b[appLocaleCode.value]) {
            return a[appLocaleCode.value].localeCompare(b[appLocaleCode.value])
        } else if (a['en'] && b['en']) return a['en'].localeCompare(b['en'])

        return 0
    }

    function resetData() {
        selectedLocationPoint.value = initLocationPoint()
    }

    function editLocation(location) {
        selectedLocationPoint.value = location
        modeSwitcher.value.edit()
    }

    function goToTable() {
        resetData()
        modeSwitcher.value.goToTable()
    }
</script>
